"use client"

import Image from "next/image"
import { HttpTypes } from "@medusajs/types"
import { convertToLocale } from "@/lib/helpers/money"
import { DeleteCartItemButton } from "@/components/molecules"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { UpdateCartItemButton } from "@/components/molecules/UpdateCartItemButton/UpdateCartItemButton"
import { useTranslate } from "@tolgee/react"
import { useAppContext } from "@/lib/context/app-context"
import { umamiTrackEvent } from "@/lib/utils/umami"
import { useIframeEvent } from "@/context/IframeContext"
import { useEffect, useState } from "react"

type LeavingMap = Record<string, boolean>

function lineUnitAmount(product: HttpTypes.StoreCartLineItem) {
  if (product.quantity > 0) {
    return product.total / product.quantity
  }
  return product.unit_price || 0
}

function lineUnitOriginal(product: HttpTypes.StoreCartLineItem) {
  if (product.quantity > 0) {
    return (product.original_total || 0) / product.quantity
  }
  return product.compare_at_unit_price || product.unit_price || 0
}

export const CartItemsProducts = ({
  products,
  currency_code,
  delete_item = true,
  change_quantity = true,
}: {
  products: HttpTypes.StoreCartLineItem[]
  currency_code: string
  delete_item?: boolean
  change_quantity?: boolean
}) => {
  const { t } = useTranslate()
  const appContext = useAppContext()
  const iframeEvent = useIframeEvent()
  const [optimisticQty, setOptimisticQty] = useState<Record<string, number>>(
    () => Object.fromEntries(products.map((p) => [p.id, p.quantity]))
  )
  const [leaving, setLeaving] = useState<LeavingMap>({})

  useEffect(() => {
    setOptimisticQty((prev) => {
      const next: Record<string, number> = {}
      for (const product of products) {
        next[product.id] = prev[product.id] ?? product.quantity
        // Prefer server quantity when it matches what we last showed, or when
        // the item is new / we have no local override pending.
        if (!(product.id in prev) || prev[product.id] === product.quantity) {
          next[product.id] = product.quantity
        }
      }
      return next
    })
  }, [products])

  return (
    <div className="divide-y px-5 border-x">
      {products.map((product) => {
        const { options } = product.variant ?? {}
        const qty = optimisticQty[product.id] ?? product.quantity
        const unit = lineUnitAmount(product)
        const unitOriginal = lineUnitOriginal(product)
        const isLeaving = !!leaving[product.id]

        const original_total = convertToLocale({
          amount: unitOriginal * qty,
          currency_code,
        })
        const total = convertToLocale({
          amount: unit * qty,
          currency_code,
        })

        const compare_at_unit_price = convertToLocale({
          amount: (product.compare_at_unit_price || 0) * qty,
          currency_code,
        })

        return (
          <div
            key={product.id}
            className={`bb-cart-item py-[12px] flex gap-2 ${
              isLeaving ? "bb-cart-item--leaving" : ""
            }`}
          >
            <LocalizedClientLink href={`/products/${product.product_handle}`}>
              <div className="w-[60px] h-[60px] flex items-center justify-center">
                {product.thumbnail ? (
                  <Image
                    src={decodeURIComponent(product.thumbnail)}
                    alt={t("product.thumbnail_alt")}
                    width={60}
                    height={60}
                    className="rounded-xs w-[60px] h-[60px] object-contain"
                  />
                ) : (
                  <Image
                    src={"/images/placeholder.svg"}
                    alt={t("product.thumbnail_alt")}
                    width={50}
                    height={66}
                    className="rounded-xs w-[50px] h-[66px] object-contain opacity-30"
                  />
                )}
              </div>
            </LocalizedClientLink>

            <div className="w-full">
              <div className="flex justify-between mb-2">
                <LocalizedClientLink
                  href={`/products/${product.product_handle}`}
                >
                  <div className="w-[280px] mb-0">
                    <h3 className="text-[14px] font-normal text-[#1e2939] uppercase truncate">
                      {product.subtitle}
                    </h3>
                  </div>
                </LocalizedClientLink>
                {delete_item && (
                  <div className="flex">
                    <DeleteCartItemButton
                      id={product.id}
                      onDeleting={() =>
                        setLeaving((prev) => ({ ...prev, [product.id]: true }))
                      }
                      onDeleteFailed={() =>
                        setLeaving((prev) => {
                          const next = { ...prev }
                          delete next[product.id]
                          return next
                        })
                      }
                      onSuccess={() => {
                        appContext.analytics?.("remove_from_cart", {
                          currency: currency_code,
                          value: total,
                          items: [
                            {
                              item_id: product.variant_sku,
                              item_name: product.title,
                              price: product.unit_price,
                            },
                          ],
                        })
                        umamiTrackEvent("remove_from_cart", appContext, {
                          currency: currency_code,
                          value: total,
                          items: [
                            {
                              item_id: product.variant_sku,
                              item_name: product.title,
                              price: product.unit_price,
                            },
                          ],
                        })
                        iframeEvent.sentRefreshDataToIframe?.()
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-col mt-0">
                <div>
                  {options?.map(({ option, id, value }) => (
                    <p key={id}>
                      {option?.title}: <span>{value}</span>
                    </p>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-2">
                  <div className="text-[14px] text-[#6a7282]">
                    {change_quantity ? (
                      <UpdateCartItemButton
                        quantity={product.quantity}
                        lineItemId={product.id}
                        onOptimisticQuantity={(next) =>
                          setOptimisticQty((prev) => ({
                            ...prev,
                            [product.id]: next,
                          }))
                        }
                      />
                    ) : (
                      <p>
                        {t("product.quantity")} <span>{product.quantity}</span>
                      </p>
                    )}
                  </div>
                  <div className="text-right block mt-0">
                    {(total !== original_total ||
                      product.compare_at_unit_price) && (
                      <p className="line-through text-[#6a7282] text-[14px]">
                        {product.compare_at_unit_price
                          ? compare_at_unit_price
                          : original_total}
                      </p>
                    )}
                    <p className="text-[14px] font-normal text-[#1e2939] tabular-nums">
                      {total}
                    </p>
                    {!!product.adjustments?.length && (
                      <p className="text-[#6a7282] text-[14px] w-full">
                        {t("product.promo_codes")}:
                        <span className="ml-1 whitespace-normal break-all">
                          {product.adjustments?.map((a) => a.code).toString()}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
